import { Injectable, Injector } from '@angular/core';
import { ComponentPortal, ComponentType } from '@angular/cdk/portal';
import { MODAL_DATA, ModalData } from '@components/abb-modal/modal';
import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import {
  SIDE_PANEL_DATA,
  SidePanelData,
} from '@components/abb-side-panel/side-panel';
import {
  AbbAlertComponent,
  AbbAlertData,
} from '@components/abb-alert/abb-alert.component';
import {
  BOTTOM_SHEET_DATA,
  BottomSheetData,
} from '@components/abb-bottom-sheet/bottom-sheet';

@Injectable({
  providedIn: 'root',
})
export class OverlayService {
  activeModalOverlay: OverlayRef | null = null;
  activeSidePanelOverlay: OverlayRef | null = null;

  constructor(private overlay: Overlay) {}

  setActiveOverlayByType(type: string, overlayRef: OverlayRef) {
    switch (type) {
      case 'modal':
        this.activeModalOverlay = overlayRef;
        break;
      case 'sidePanel':
        this.activeSidePanelOverlay = overlayRef;
        break;
      default:
        break;
    }
  }

  openAlert(data: Omit<AbbAlertData, 'onConfirm' | 'onCancel'>) {
    const promise = new Promise<void>((res, rej) => {
      this.openModal(
        AbbAlertComponent,
        {
          ...data,
          onConfirm: () => {
            res();
          },
          onCancel: () => {
            rej();
          },
        },
        { hasBackdrop: true }
      );
    });
    return promise;
  }

  openSidePanel<T extends SidePanelData<any>>(
    component: ComponentType<T>,
    data: T extends SidePanelData<infer TData> ? TData : never,
    {
      panelClass,
      ...options
    }: Omit<OverlayConfig, 'positionStrategy' | 'hasBackdrop'> = {}
  ) {
    const overlayRef = this.overlay.create({
      hasBackdrop: false,
      positionStrategy: this.overlay
        .position()
        .global()
        .top('64px')
        .bottom('0px')
        .right('0px'),
      panelClass: ['max-w-screen', 'flex', 'flex-col'].concat(panelClass ?? []),
      ...options,
    });
    this.setActiveOverlayByType('sidePanel', overlayRef);
    const portal = new ComponentPortal(
      component,
      undefined,
      Injector.create({
        providers: [
          {
            provide: OverlayRef,
            useValue: overlayRef,
          },
          {
            provide: SIDE_PANEL_DATA,
            useValue: data,
          },
        ],
      })
    );
    const componentRef = overlayRef.attach(portal);
    return {
      overlayRef,
      componentInstance: componentRef.instance,
      componentRef: componentRef,
    };
  }

  openModal<T extends ModalData<any>>(
    component: ComponentType<T>,
    data: T extends ModalData<infer TData> ? TData : never,
    {
      panelClass,
      hasBackdrop = true,
      ...options
    }: Omit<OverlayConfig, 'positionStrategy'> = {}
  ) {
    const positionStrategy = this.overlay
      .position()
      .global()
      .centerHorizontally()
      .centerVertically();

    const overlayRef = this.overlay.create({
      hasBackdrop,
      positionStrategy,
      panelClass: ['max-h-screen', 'max-w-screen', 'flex', 'flex-col'].concat(
        panelClass ?? []
      ),
      ...options,
    });

    this.setActiveOverlayByType('modal', overlayRef);

    const portal = new ComponentPortal(
      component,
      undefined,
      Injector.create({
        providers: [
          { provide: OverlayRef, useValue: overlayRef },
          { provide: MODAL_DATA, useValue: data },
        ],
      })
    );

    const componentRef = overlayRef.attach(portal);

    // ⛔️ Se vuoi evitare l’uso di `requestAnimationFrame` + resize, puoi rimuovere questo blocco

    return {
      overlayRef,
      componentInstance: componentRef.instance,
    };
  }

  openBottomSheet<T extends BottomSheetData<any>>(
    component: ComponentType<T>,
    data: T extends BottomSheetData<infer TData> ? TData : never,
    {
      panelClass,
      ...options
    }: Omit<OverlayConfig, 'positionStrategy' | 'hasBackdrop'> = {}
  ) {
    const overlayRef = this.overlay.create({
      hasBackdrop: false,
      positionStrategy: this.overlay
        .position()
        .global()
        .left('24px')
        .right('40px')
        .bottom('0px'),
      panelClass: ['max-w-screen', 'flex', 'flex-row', 'mr-10'].concat(
        panelClass ?? []
      ),
      ...options,
    });
    const portal = new ComponentPortal(
      component,
      undefined,
      Injector.create({
        providers: [
          {
            provide: OverlayRef,
            useValue: overlayRef,
          },
          {
            provide: BOTTOM_SHEET_DATA,
            useValue: data,
          },
        ],
      })
    );
    const componentRef = overlayRef.attach(portal);
    return {
      overlayRef,
      componentInstance: componentRef.instance,
    };
  }
}
